import type { AutomationEntityUpdateLogEntry } from '@repo/api-codegen';
import { useApiAutomationGetEntityUpdateLog } from '@repo/api-codegen';
import type { IBaseModel } from '@repo/common/models/baseModel.ts';
import { useCallback, useMemo } from 'react';
import type { FetchModelListHook } from '../../api/factories/types.ts';
import {
	formatAutomationAction,
	formatAutomationActivityLog,
} from '../AutomationV2/utils.ts';
import { TableV2 } from '../TableV2';
import { useHistoryTableColumns } from './hooks';
import { getPropagateFrom } from './utils.ts';

const joinWithAnd = (items: string[] | undefined): string => {
	if (!items || items.length === 0) return '';
	if (items.length === 1) return items[0];
	if (items.length === 2) return `${items[0]} and ${items[1]}`;
	const allButLast = items.slice(0, -1).join(', ');
	const last = items[items.length - 1];
	return `${allButLast} and ${last}`;
};

const buildChangesText = (record: AutomationEntityUpdateLogEntry): string => {
	const changes: string[] = [];

	for (const update of record.updates ?? []) {
		changes.push(formatAutomationAction(update.type, update.value));
	}

	if (record.propagate_updates) {
		const from = getPropagateFrom(record.propagate_updates.to);
		changes.push(
			`Propagated ${joinWithAnd(record.propagate_updates.fields)} from ${from}`
		);
	}

	// Mirror the table render: only fall back to activity logs when there are
	// no other changes to display.
	if (record.activity_logs && changes.length === 0) {
		for (const activityLog of record.activity_logs) {
			const formatted = formatAutomationActivityLog(
				activityLog.field,
				activityLog.value
			);
			if (formatted) changes.push(formatted);
		}
	}

	return changes.join('\n');
};

interface AutomationHistoryTableProps {
	automationJobId: string;
}

const COLUMN_VISIBILITY = {
	catalogType: 'automation_history',
	catalogServerType: 'automation_history',
} as const;

export function AutomationHistoryDetailsTable({
	automationJobId,
}: AutomationHistoryTableProps) {
	const columns = useHistoryTableColumns();

	const defaultSearchParams = useMemo(
		() => ({
			automation_job_id: automationJobId,
		}),
		[automationJobId]
	);

	const csvTransform = useCallback(
		(record: AutomationEntityUpdateLogEntry & IBaseModel) => ({
			Resource: record.entity?.title_cased ?? record.entity?.title ?? '',
			'Resource ID': record.entity?.id ?? '',
			'Resource type': record.entity?.entity_type ?? '',
			Changes: buildChangesText(record),
		}),
		[]
	);

	return (
		<TableV2
			columns={columns}
			usePaginationList={
				useApiAutomationGetEntityUpdateLog as unknown as FetchModelListHook<
					AutomationEntityUpdateLogEntry & IBaseModel
				>
			}
			usePaginationListOptions={{
				pathParams: { jobId: automationJobId },
			}}
			useCodegenListInterface={true}
			defaultRequiredSearchParams={defaultSearchParams}
			columnVisibility={COLUMN_VISIBILITY}
			withInteractiveHeader={false}
			withSearch
			withCsvExport
			csvTransform={csvTransform}
			withInfiniteScroll={false}
			noRecordsText="No history found"
		/>
	);
}
